import { useMemo, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Table from 'react-bootstrap/Table';
import { useMedia } from 'react-use';

import { Button } from 'modules/core/components/buttons';
import { PriceListItem } from '../store/priceListItemsStore';

interface PriceListViewStorageTableProps {
  items: PriceListItem[];
}

interface StorageRow {
  vendorCode: string;
  positionName: string;
  storedObjectName?: string;
  byDiameter: Map<string, PriceListItem>;
}

// Стили — те же, что у «Розничных цен» в PriceListViewTable (единый вид
// таблиц прайса): fixed-раскладка, sticky-шапка, серые расчётные ячейки
const TABLE_STYLES = `
  .price-list-table { margin-bottom: 0; table-layout: fixed; }
  .price-list-table td, .price-list-table th {
    padding: 0 !important;
    line-height: 1.1;
    vertical-align: middle;
  }
  .price-list-table thead th {
    height: 50px !important;
    vertical-align: middle !important;
    text-align: center !important;
    padding: 0.75rem 0.5rem !important;
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: #e9ecef;
    font-size: 0.95rem;
  }
  .price-list-table tbody tr:hover td:first-child { background-color: #f1f3f5; }
  .price-list-table td.position-cell {
    padding: 0.4rem 0.6rem !important;
    background-color: #fafbfc;
  }
  .price-list-table td.calc-cell {
    padding: 0.4rem 0.6rem !important;
    background-color: #f8f9fa;
    text-align: center;
  }
  @media (max-width: 991px) {
    .price-list-table { min-width: 640px; border-collapse: separate; border-spacing: 0; }
    .price-list-table th, .price-list-table td { border: 0; border-bottom: 1px solid #dee2e6; }
    .price-list-table th:not(:last-child),
    .price-list-table td:not(:last-child) { border-right: 1px solid #dee2e6; }
    .price-list-table th:first-child,
    .price-list-table td:first-child { position: sticky; left: 0; z-index: 1; }
    .price-list-table thead th:first-child { z-index: 3; }
  }
`;

const sortDiameters = (diameters: string[]): string[] => {
  return [...diameters].sort((a, b) => {
    const numA = parseInt(a.replace(/\D/g, ''), 10);
    const numB = parseInt(b.replace(/\D/g, ''), 10);
    return (numA || 0) - (numB || 0) || a.localeCompare(b);
  });
};

// Колонка для позиций без диаметра (единая цена на все R): последняя, подпись «—»
const NO_DIAMETER_KEY = '';
const NO_DIAMETER_TITLE = '—';

// ×6/×12 на дробных ценах дают артефакты float (83.33 × 6 = 499.98000…05)
const roundPrice = (value: number): number => Math.round(value * 100) / 100;

// Read-only матрица «позиция хранения × диаметр» для просмотра прайса офиса:
// строки — позиции прайса хранения (69), при нескольких типах хранимого объекта
// у одной позиции — строка на каждый объект; колонки — диаметры R12–R24.
// Классификаторы прайса хранения отличаются от шиномонтажа (classifier2 —
// тип хранимого объекта, а не тип автомобиля), поэтому фильтр «Тип автомобиля»
// и быстрый поиск модалки на этот блок не действуют
const PriceListViewStorageTable = (props: PriceListViewStorageTableProps) => {
  const { items } = props;

  // Базис цены — как «4/1 колеса» у комплексов; срок — 1/6/12 месяцев.
  // Цена прайса хранения задана за 1 колесо в месяц, ячейка = цена × колёса × месяцы.
  // 6 месяцев — сезонный стандарт (как в снапшоте прайсбука бека)
  const [wheelMode, setWheelMode] = useState<'4' | '1'>('4');
  const [monthsMode, setMonthsMode] = useState<1 | 6 | 12>(6);
  const priceMultiplier = (wheelMode === '4' ? 4 : 1) * monthsMode;

  // Планшет/телефон: колонка «Позиция» уже (250px не влезает), матрица скроллится
  const isTabMobile = useMedia('(max-width: 991px)');

  const rows = useMemo<StorageRow[]>(() => {
    const allRows = new Map<string, StorageRow>();
    // сколько типов объектов у каждой позиции — решает, дописывать ли объект к строке
    const objectsByPosition = new Map<string, Set<string>>();

    items.forEach((item) => {
      const object = item.classifier2RefValueName;
      const positionKey = `${ item.positionVendorCode }__${ item.positionShortName }`;
      if (!objectsByPosition.has(positionKey)) objectsByPosition.set(positionKey, new Set());
      if (object) objectsByPosition.get(positionKey).add(object);

      const key = `${ positionKey }__${ object ?? '' }`;
      let row = allRows.get(key);
      if (!row) {
        row = {
          vendorCode: item.positionVendorCode,
          positionName: item.positionShortName,
          storedObjectName: object,
          byDiameter: new Map(),
        };
        allRows.set(key, row);
      }
      if (item.classifier1RefValueName) {
        row.byDiameter.set(item.classifier1RefValueName, item);
      } else {
        row.byDiameter.set(NO_DIAMETER_KEY, item);
      }
    });

    // позиция без единой проставленной цены (все ячейки «—») не показывается;
    // если таких все — rows пуст и блок скрывается целиком
    return Array.from(allRows.values())
      .filter(row => Array.from(row.byDiameter.values()).some(item => item?.price != null))
      .map((row) => ({
        ...row,
        // один объект у позиции — не дублируем его в подписи строки («Хранение»);
        // несколько — уточняем («Хранение — Диски»), иначе цены неразличимы
        storedObjectName: objectsByPosition
          .get(`${ row.vendorCode }__${ row.positionName }`)?.size > 1
          ? row.storedObjectName
          : undefined,
      }));
  }, [items]);

  const displayedDiameters = useMemo(() => {
    const set = new Set<string>();
    items.forEach((item) => { set.add(item.classifier1RefValueName ?? NO_DIAMETER_KEY); });
    const diameters = sortDiameters(Array.from(set).filter(d => d !== NO_DIAMETER_KEY));
    return set.has(NO_DIAMETER_KEY) ? [...diameters, NO_DIAMETER_KEY] : diameters;
  }, [items]);

  if (rows.length === 0) return null;

  return (
    <>
      <style>{ TABLE_STYLES }</style>

      <Row className='mt-4 mb-3 align-items-center'>
        <Col md={ 8 } className='d-flex align-items-center flex-wrap'>
          <h5 className='mb-0 font-weight-bold'>Цены хранения</h5>
          { /* Переключатели базиса — по образцу «4/1 колеса» у «Цен комплексов»
              (btn-group + core Button): цена показывается за выбранные колёса × срок */ }
          <div className='btn-group btn-group-sm ml-3 mr-3' role='group' aria-label='Кол-во колёс'>
            <Button
              variant='outline-primary'
              active={ wheelMode === '4' }
              text='4 колеса'
              onClick={ () => setWheelMode('4') }
            />
            <Button
              variant='outline-primary'
              active={ wheelMode === '1' }
              text='1 колесо'
              onClick={ () => setWheelMode('1') }
            />
          </div>
          <div className='btn-group btn-group-sm' role='group' aria-label='Срок хранения'>
            <Button
              variant='outline-primary'
              active={ monthsMode === 1 }
              text='1 месяц'
              onClick={ () => setMonthsMode(1) }
            />
            <Button
              variant='outline-primary'
              active={ monthsMode === 6 }
              text='6 месяцев'
              onClick={ () => setMonthsMode(6) }
            />
            <Button
              variant='outline-primary'
              active={ monthsMode === 12 }
              text='12 месяцев'
              onClick={ () => setMonthsMode(12) }
            />
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg={ 12 }>
          <div className='table-responsive'>
            <Table className='price-list-table table-bordered table-hover'>
              <thead className='thead-light'>
                <tr>
                  <th style={ { width: isTabMobile ? '130px' : '250px' } }>Позиция</th>
                  { displayedDiameters.map((diameter) => (
                    <th key={ diameter }>
                      { diameter === NO_DIAMETER_KEY ? NO_DIAMETER_TITLE : diameter }
                    </th>
                  )) }
                </tr>
              </thead>
              <tbody>
                { rows.map((row) => (
                  <tr key={ `${ row.vendorCode }__${ row.positionName }__${ row.storedObjectName ?? '' }` }>
                    <td className='font-weight-bold position-cell'>
                      { row.storedObjectName
                        ? `${ row.positionName } — ${ row.storedObjectName }`
                        : row.positionName }
                    </td>
                    { displayedDiameters.map((diameter) => {
                      const item = row.byDiameter.get(diameter);
                      return (
                        <td key={ diameter } className='calc-cell'>
                          { item?.price != null ? roundPrice(item.price * priceMultiplier) : '—' }
                        </td>
                      );
                    }) }
                  </tr>
                )) }
              </tbody>
            </Table>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default PriceListViewStorageTable;
